'use client';

export type ClientVisibleResponseStart = {
  clientInputId: string;
  monotonicStartedAt: number;
  wallStartedAt: number;
  visibilityInterrupted: boolean;
  submitToMutationDispatchMs?: number;
  submitToMutationAckMs?: number;
};

export type ClientVisibleResponseMeasurement =
  | {
      presentationOutcome: 'interrupted_hidden';
      submitToMutationDispatchMs?: number;
      submitToMutationAckMs?: number;
    }
  | {
      presentationOutcome: 'painted_assistant_content';
      enterToFirstVisibleMs: number;
      submitToMutationDispatchMs?: number;
      submitToMutationAckMs?: number;
    };

const pendingStarts = new Map<string, ClientVisibleResponseStart>();
const MAX_PENDING_STARTS = 50;
const MAX_PENDING_AGE_MS = 10 * 60_000;

function prunePendingStarts(now: number): void {
  for (const [inputId, start] of pendingStarts) {
    if (now - start.monotonicStartedAt > MAX_PENDING_AGE_MS) {
      pendingStarts.delete(inputId);
    }
  }
  while (pendingStarts.size >= MAX_PENDING_STARTS) {
    const oldestInputId = pendingStarts.keys().next().value;
    if (typeof oldestInputId !== 'string') break;
    pendingStarts.delete(oldestInputId);
  }
}

export function registerClientVisibleResponseStart(
  start: Omit<ClientVisibleResponseStart, 'visibilityInterrupted'>
): void {
  if (
    !Number.isFinite(start.monotonicStartedAt) ||
    !Number.isFinite(start.wallStartedAt)
  )
    return;
  prunePendingStarts(start.monotonicStartedAt);
  pendingStarts.set(start.clientInputId, {
    ...start,
    visibilityInterrupted:
      typeof document !== 'undefined' && document.visibilityState !== 'visible',
  });
  try {
    performance.mark?.(`org-chat-submit:${start.clientInputId}`, {
      startTime: start.monotonicStartedAt,
    });
  } catch {
    // Diagnostic marks must never block a send.
  }
}

export function hasClientVisibleResponseStart(clientInputId: string): boolean {
  return pendingStarts.has(clientInputId);
}

export function aliasClientVisibleResponseStart(
  sourceClientInputId: string,
  targetClientInputId: string
): boolean {
  const source = pendingStarts.get(sourceClientInputId);
  if (!source) return false;
  pendingStarts.set(targetClientInputId, {
    ...source,
    clientInputId: targetClientInputId,
  });
  return true;
}

export function markClientMutationDispatch(
  clientInputId: string,
  at: number
): void {
  const start = pendingStarts.get(clientInputId);
  const elapsed = start ? at - start.monotonicStartedAt : Number.NaN;
  if (
    !start ||
    !Number.isFinite(elapsed) ||
    elapsed < 0 ||
    start.submitToMutationDispatchMs !== undefined
  )
    return;
  start.submitToMutationDispatchMs = elapsed;
}

export function markClientMutationAck(clientInputId: string, at: number): void {
  const start = pendingStarts.get(clientInputId);
  const elapsed = start ? at - start.monotonicStartedAt : Number.NaN;
  if (
    !start ||
    !Number.isFinite(elapsed) ||
    elapsed < 0 ||
    start.submitToMutationAckMs !== undefined
  )
    return;
  start.submitToMutationAckMs = elapsed;
}

export function markClientVisibleResponseVisibilityInterrupted(): void {
  for (const start of pendingStarts.values())
    start.visibilityInterrupted = true;
}

export function consumeClientVisibleResponseStart(
  clientInputId: string
): ClientVisibleResponseStart | null {
  const start = pendingStarts.get(clientInputId) ?? null;
  pendingStarts.delete(clientInputId);
  return start;
}

export function createClientVisibleResponseMeasurement(
  start: ClientVisibleResponseStart,
  monotonicVisibleAt: number,
  currentlyVisible: boolean
): ClientVisibleResponseMeasurement | null {
  if (start.visibilityInterrupted || !currentlyVisible) {
    return {
      presentationOutcome: 'interrupted_hidden',
      ...mutationBounds(start),
    };
  }
  const elapsed = monotonicVisibleAt - start.monotonicStartedAt;
  if (!Number.isFinite(elapsed) || elapsed < 0) return null;
  return {
    presentationOutcome: 'painted_assistant_content',
    enterToFirstVisibleMs: elapsed,
    ...mutationBounds(start),
  };
}

function mutationBounds(start: ClientVisibleResponseStart) {
  return {
    ...(start.submitToMutationDispatchMs !== undefined
      ? { submitToMutationDispatchMs: start.submitToMutationDispatchMs }
      : {}),
    ...(start.submitToMutationAckMs !== undefined
      ? { submitToMutationAckMs: start.submitToMutationAckMs }
      : {}),
  };
}

export function resetClientVisibleResponseTimingsForTest(): void {
  pendingStarts.clear();
}
